import { flash, FormGroup, Label, Modal, SubmitButton, Textarea, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { requiredString } from "common";
import { Field, FieldProps } from "formik";
import React, { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { Event, Experience, Timeslot } from "types";
import { object } from "yup";
import { api } from "../../api/api";
import { useInvalidate } from "../../hooks/useInvalidation";
import { useResource } from "../../hooks/useResource";
import { updateCache } from "../../utils/updateCache";
import { Form } from "../Common/Form";
import { NoTranslate } from "../Common/NoTranslate";
import { ProductImage } from "../Common/ProductImage";
import { SecondaryButton } from "../Common/SecondaryButton";
import { XolaFormik } from "../Common/XolaFormik";
import { Loader } from "../Loader";
import { EventSummaryTile } from "./EventSummaryTile";

const validationSchema = object().shape({
    text: requiredString(),
});

interface EventNotePayload {
    text: string;
}

interface CreateEventNoteModalProps {
    events: Event[] | undefined;
    timeslots?: Timeslot[];
    timeslot?: Timeslot;
    onClose: (e: any) => void;
    isOpen: boolean;
}

export const CreateEventNoteModal = ({ events, timeslots, timeslot, onClose, isOpen }: CreateEventNoteModalProps) => {
    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <ModalContent events={events!} timeslots={timeslots} timeslot={timeslot} onClose={onClose} />
        </Modal>
    );
};

interface ModalContentProps {
    events: Event[];
    timeslots?: Timeslot[];
    timeslot?: Timeslot;
    onClose: (e?: any) => void;
}

const ModalContent = ({ events, timeslots, timeslot, onClose }: ModalContentProps) => {
    const queryClient = useQueryClient();
    const invalidate = useInvalidate();
    const [isLoading, setIsLoading] = useState(false);

    // isLoading on mutation won't work here, since we are not awaiting for response in this function.
    const { mutateAsync: addEventNote } = useMutation(async (payload: EventNotePayload) => {
        return events.map(async (event) => api.post(`/events/${event.id}/notes`, payload));
    });

    const handleSubmit = async (values: any) => {
        const payload = {
            text: values.text.trim(),
        };
        setIsLoading(true);
        try {
            await addEventNote(payload);

            /**
             * sometimes it takes longer for the note to be created and added to the purchase / event,
             * and this causes the old set of notes returned
             */
            setTimeout(async () => {
                queryClient.invalidateQueries(["events", "collection"]);
                queryClient.invalidateQueries(["events", events]);
                invalidate("purchaseItems", "collection");

                if (timeslot) {
                    updateCache("timeslots", { ...timeslot, noteCount: timeslot.noteCount + 1 }, false);
                } else {
                    invalidate("timeslots");
                }

                Promise.all(events.map(async (event) => invalidate("events", event.id)));
                setIsLoading(false);
                onClose();
            }, 1000);
        } catch {
            flash.danger("Error in creating event note.");
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const textField = document.querySelector("#text-field")!;

        if (textField) {
            (textField as HTMLInputElement)?.focus();
        }
    }, []);

    return (
        <XolaFormik
            validateOnChange
            initialValues={{ text: "" }}
            validateOnBlur={false}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
        >
            {({ handleSubmit }) => (
                <Form>
                    <Modal.Header>Add Event Note</Modal.Header>

                    <Modal.Body>
                        {events ? events.map((event) => <EventSummaryTile key={event.id} event={event} />) : null}
                        {timeslots?.map((timeslot) => (
                            <DisabledProductImage key={timeslot.id} timeslot={timeslot} />
                        ))}
                        <fieldset className="mt-10" disabled={isLoading}>
                            <Field name="text">
                                {({ meta }: FieldProps) => (
                                    <FormGroup>
                                        <Label className={clsx(meta.error && "text-danger")}>
                                            Note
                                            {meta.error ? <span className="ml-1 text-danger">{meta.error}</span> : null}
                                        </Label>
                                        <Field
                                            isRequired
                                            id="text-field"
                                            className="h-28"
                                            name="text"
                                            isError={!!meta.error}
                                            as={Textarea}
                                            placeholder="Message"
                                        />
                                    </FormGroup>
                                )}
                            </Field>
                        </fieldset>
                    </Modal.Body>

                    <Modal.Footer className="space-x-4">
                        <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                        <SubmitButton isLoading={isLoading} onClick={handleSubmit}>
                            Add
                        </SubmitButton>
                    </Modal.Footer>
                </Form>
            )}
        </XolaFormik>
    );
};

interface DisabledProductImageProps {
    timeslot: Timeslot;
}

const DisabledProductImage = ({ timeslot }: DisabledProductImageProps) => {
    const { data: experience, isLoading: isLoadingProduct } = useResource<Experience>(["products", timeslot.product]);

    if (isLoadingProduct) {
        return <Loader />;
    }

    return (
        <Tooltip content="Event does not have a confirmed booking">
            <div className="mb-5 flex space-x-1 rounded-lg bg-gray-lighter p-4">
                <ProductImage size="small" className="rounded-lg" product={experience!} />
                <div className="w-full space-y-2">
                    <NoTranslate as="div" className="text-base font-bold leading-4">
                        {experience?.name}
                    </NoTranslate>
                    <div className="flex space-x-5 text-gray-darker">0 Reserved</div>
                </div>
            </div>
        </Tooltip>
    );
};
